import React, { useMemo } from 'react';
import { View } from 'react-native';
import { Text } from '@/components/gluestack/text';
import { useTranslation } from 'react-i18next';
import { BillingService } from '@/services/domain/BillingService';
import { TransactionHistoryView, TransactionHistoryItem } from '@/models/Billing';
import dayjs from '@/i18n/dayjs-config';
import { Notebook02Icon } from '@hugeicons-pro/core-stroke-rounded';
import { useCommunity } from '@/hooks/useCommunity';
import { useAuth } from '@/components/providers/AuthProvider';
import { Badge, BadgeText } from '@/components/gluestack/badge';
import { VStack } from '@/components/gluestack/vstack';
import { DataList } from '@/components/ui/DataList';
import { getNextPageSkipOrCursor, buildPaginationParams } from '@/hooks/usePaginationUtils';
import { useSubscribeInfo } from '@/hooks/useSubscribeInfo';
import { PremiumStatusCard } from '@/components/domain/account/PremiumStatusCard';
import { useRegisterRightPanelSlot } from '@/components/layout/RightPanelSlotContext';

export default function TransactionsScreen() {
  const { t } = useTranslation();
  const community = useCommunity();
  const { currentCommunity } = useAuth();
  const { data: subscribeInfo } = useSubscribeInfo();

  // Premium status only in the right panel (wide screens); without it the list stands alone
  const premiumEndsOnUtc = subscribeInfo?.IsPremium ? subscribeInfo.PremiumEndsOnUtc : null;
  const rightPanelContent = useMemo(
    () =>
      premiumEndsOnUtc && community?.premiumPlan ? (
        <PremiumStatusCard
          premiumEndsOnUtc={premiumEndsOnUtc}
          premiumPlanName={community.premiumPlan}
          compact
        />
      ) : null,
    [premiumEndsOnUtc, community?.premiumPlan]
  );
  useRegisterRightPanelSlot(rightPanelContent, 'wide');

  return (
    <View className="flex-1">
      <DataList<TransactionHistoryView, TransactionHistoryItem>
        title={t('account.transactions.title')}
        queryKey={['billing', 'transactions', currentCommunity]}
        queryFn={async ({ pageParam }) =>
          await BillingService.getTransactionHistory(buildPaginationParams(pageParam, 25))
        }
        getNextPageParam={getNextPageSkipOrCursor}
        getPageItems={(page) => page.Items}
        getItemKey={(item) => String(item.Id)}
        emptyMessage={t('account.transactions.empty')}
        emptyIcon={Notebook02Icon}
        columns={[
          {
            width: 'w-32',
            renderHeader: () => (
              <Text className="text-left font-extrabold leading-6 text-typography-800">
                {t('account.transactions.header.date')}
              </Text>
            ),
            renderContent: (item) => (
              <>
                <Text size="sm" bold className="leading-5 text-typography-800">
                  {dayjs(item.Date).format('YYYY-MM-DD')}
                </Text>
                {item.TransactionType !== 'GiftCertificatePaidFor' &&
                item.Days !== null &&
                item.Days !== undefined ? (
                  <View className="mt-1">
                    <Badge
                      action={item.Days < 0 ? 'error' : 'success'}
                      variant="solid"
                      size="sm"
                      className="w-auto self-start">
                      <BadgeText size="md">
                        <BadgeText size="md" className="font-bold">
                          {item.Days > 0 ? `+${item.Days}` : item.Days}
                        </BadgeText>{' '}
                        {t('account.transactions.days', { count: Math.abs(item.Days ?? 0) })}
                      </BadgeText>
                    </Badge>
                  </View>
                ) : null}
              </>
            ),
          },
          {
            width: 'flex-1',   
            renderHeader: () => (
              <Text className="text-left font-extrabold leading-6 text-typography-800">
                {t('account.transactions.header.description')}
              </Text>
            ),
            renderContent: (item) => {
              const hidePaymentValues =
                item.TransactionType === 'PaidWithApple' || item.TransactionType === 'PaidWithGoogle';
              const showAmountBadge =
                !hidePaymentValues && item.Amount !== null && item.Amount !== undefined && !!item.Currency;

              return (
                <VStack className="gap-1">
                  <Text className="leading-5 text-typography-800">
                    {t(`TransactionType.${item.TransactionType}`, {
                      ns: 'domain',
                      premium: community?.premiumPlan,
                      defaultValue: item.TransactionType,
                    })}
                  </Text>
                  <View className="flex-row w-full flex-wrap items-center gap-2">
                    {showAmountBadge ? (
                      <Badge action="muted" variant="solid" size="sm">
                        <BadgeText size="md">{`${item.Amount} ${item.Currency}`}</BadgeText>
                      </Badge>
                    ) : null}
                    {item.TransactionId ? (
                      <Badge action="muted" variant="solid" size="sm">
                        <BadgeText size="md">{item.TransactionId}</BadgeText>
                      </Badge>
                    ) : null}
                  </View>
                </VStack>
              );
            },
          },
        ]}
      />
    </View>
  );
}
